import { Routes, Route, Navigate } from 'react-router-dom'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import GameScoresPage from './pages/GameScoresPage'
import MatchDetailsPage from './pages/MatchDetailsPage'
import ProfileEditPage from './pages/ProfileEditPage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import EmailVerificationRequiredPage from './pages/EmailVerificationRequiredPage'
import MatchClaimPage from './pages/MatchClaimPage'
import LocationPage from './pages/LocationPage'
import AdminConsolePage from './pages/AdminConsolePage'
import { useAuth } from './hooks/useAuth'

// Protected Route component - requires authentication AND email verification
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Loading...</div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  // Check if email is verified
  if (user && !user.emailVerified) {
    return <Navigate to="/verify-email-required" replace />
  }
  
  return <>{children}</>
}

// Route that requires authentication but NOT email verification (for email verification flow)
function AuthenticatedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Loading...</div>
      </div>
    )
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth()

  if (loading) {
    return <div className="min-h-screen bg-firedup-dark flex items-center justify-center">Loading...</div>
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }
  if (!user?.roles?.includes('admin')) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

// Redirect authenticated users away from auth pages
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Loading...</div>
      </div>
    )
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>
}

function App() {
  const { loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Loading...</div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route
        path="/login" 
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <AuthRoute>
            <RegisterPage />
          </AuthRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <AuthRoute>
            <ForgotPasswordPage />
          </AuthRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <AuthRoute>
            <ResetPasswordPage />
          </AuthRoute>
        } 
      />
      <Route 
        path="/verify-email" 
        element={<VerifyEmailPage />} 
      />
      <Route 
        path="/claim/:matchId" 
        element={<MatchClaimPage />} 
      />
      <Route 
        path="/verify-email-required" 
        element={
          <AuthenticatedRoute>
            <EmailVerificationRequiredPage />
          </AuthenticatedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin"
        element={
          <AdminRoute>
            <AdminConsolePage />
          </AdminRoute>
        }
      />
      <Route
        path="/game/:gameId/scores"
        element={
          <ProtectedRoute>
            <GameScoresPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/match-details/:matchId" 
        element={
          <ProtectedRoute>
            <MatchDetailsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile/edit" 
        element={
          <ProtectedRoute>
            <ProfileEditPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/locations" 
        element={
          <ProtectedRoute>
            <LocationPage />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  )
}

export default App
